import { Content, Editor, EditorOptions } from '@tiptap/react';
import React, { forwardRef, MutableRefObject, useEffect } from 'react';

import { useEventListener } from '@bonusly/events';

import { ActionInputContextProvider } from '../context';
import { AudioRecordingEvent, VideoRecordingEvent } from '../events';
import { useActionInputState } from '../hooks/use-action-input-state';
import { useFileUpload } from '../hooks/use-file-upload';
import { useLinkPreview } from '../hooks/use-link-preview';
import { ModuleKey, RichInputHandle, useRichInput } from '../hooks/use-rich-input';
import {
	ActionData,
	AudioAttachment,
	Budget,
	EmployeeSpotlightAttachment,
	ImageAttachment,
	LinkPreviewAttachment,
	SocialAttachment,
	User,
	VideoAttachment,
} from '../lib/entities';
import { RichMediaState } from '../lib/state';
import { ActionValidator, ValidationError } from '../lib/validation';
import { ActionModule } from '../types';
import { loadFileFromBrowserStorage } from '../utils/video-storage';
import { ActionInputHandle, useActionHandle } from './input/hooks/use-action-handle';
import { getContextProps } from './input/utils/get-context-props';
import { Privacy } from './privacy/types';

export type { ActionInputHandle };

type Fetcher<T> = (config: { editor: Editor; query: string; state: RichMediaState }) => Promisable<T>;

export type ActionInputProps = Partial<EditorOptions> & {
	allowanceBoostId?: string;
	autofocus?: boolean | 'end';
	budget?: Budget;
	calculateReceiversFn?: (includeGiver: boolean) => number;
	calculateTotalFn?: (data: RichMediaState, state: { includeGiver: boolean }) => number;
	children?: React.ReactNode;
	content?: Content;
	context: string;
	editable?: boolean;
	fetchMentions?: Fetcher<User[]>;
	fetchRecipients?: Fetcher<User[]>;
	giveAmount?: number;
	initialAttachment?: SocialAttachment | ImageAttachment | VideoAttachment | EmployeeSpotlightAttachment | LinkPreviewAttachment | AudioAttachment | null;
	initialIncludeGiver?: boolean;
	initialPrivacy?: Privacy;
	initialPrivacyButtonEnabled?: boolean;
	isSubmitting?: boolean;
	isLoadingAttachment: boolean;
	modules: ActionModule[];
	onStartDraft?: VoidFunction;
	onSubmit: (data: ActionData) => void;
	onUpload?: (mediaFile: File, posterFile?: File | null) => Promisable<VideoAttachment | ImageAttachment>;
	onValidationError?: (errors: ValidationError[]) => void;
	options?: Partial<Record<ModuleKey, any>>;
	placeholder?: string | null;
	postFile?: (file: File) => void;
	ref?: MutableRefObject<RichInputHandle | null>;
	validator?: ActionValidator;
	cancelVideoGeneration?: () => void;
};

export const ActionInput = forwardRef<ActionInputHandle, ActionInputProps>(
	(
		{
			calculateTotalFn,
			calculateReceiversFn,
			children,
			initialAttachment,
			initialIncludeGiver,
			initialPrivacy,
			initialPrivacyButtonEnabled,
			onStartDraft,
			...rest
		},
		ref
	) => {
		const state = useActionInputState({
			initialAttachment,
			initialIncludeGiver,
			initialPrivacy,
			initialPrivacyButtonEnabled,
			onStartDraft,
		});

		const createAttachment = useFileUpload({
			onUpload: rest.onUpload,
			afterUpload: state.setAttachment,
		});
		const input = useRichInput({
			postFile: createAttachment,
			...(rest as any),
		});

		// Auto-detect URLs and fetch link previews
		useLinkPreview({
			editor: input.editor,
			attachment: state.attachment,
			setAttachment: state.setAttachment,
			enabled: true,
		});

		// Shared event handler for both initial load and manual attach
		useEventListener(VideoRecordingEvent.AttachToBonus, async (eventData: { structuredContent?: any[] }) => {
			const { structuredContent } = eventData || {};
			if (structuredContent && input.editor?.isEmpty) {
				input.editor?.chain().insertContent(structuredContent).focus().run();
			}
			const loadedFile = await loadFileFromBrowserStorage('video');
			const posterFile = await loadFileFromBrowserStorage('poster');
			if (loadedFile) {
				createAttachment(loadedFile, posterFile);
			}
		});

		// Audio recording event handler
		useEventListener(AudioRecordingEvent.AttachToBonus, (eventData: { attachment?: AudioAttachment }) => {
			const { attachment } = eventData || {};
			if (attachment) {
				state.setAttachment(attachment);
			}
		});

		const inputState = RichMediaState.parse(input.editor);

		useActionHandle(ref, {
			editor: input.editor,
			inputState,
			reset: state.reset,
			setErrors: state.setErrors,
			state,
		});

		// Any time the user edits text (via paste, type, or button click)
		// trigger the startDraft callback
		useEffect(() => {
			if (input.editor?.getText()) {
				state.startDraft();
			}
		}, [input.editor, state]);

		return (
			<ActionInputContextProvider
				value={{
					calculateTotalFn,
					calculateReceiversFn,
					onUpload: createAttachment,
					state: inputState,
					...getContextProps(rest),
					...state,
					...input,
				}}
			>
				{children}
			</ActionInputContextProvider>
		);
	}
);

ActionInput.displayName = 'ActionInput';
